/**
 * Quick Search Popup Component
 * Horizontal popup for quick partner search from dashboard
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Search, X, RefreshCw } from 'lucide-react';

interface QuickSearchPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const searchOptions = {
  religions: ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Jain', 'Buddhist', 'Other'],
  castes: ['Brahmin', 'Kshatriya', 'Vaishya', 'Shudra', 'Patel', 'Gupta', 'Singh', 'Jain', 'Other'],
  motherTongues: ['Hindi', 'English', 'Bengali', 'Tamil', 'Telugu', 'Marathi', 'Gujarati', 'Punjabi', 'Other'],
  professions: ['Doctor', 'Engineer', 'Teacher', 'CA', 'Business', 'Government Job', 'Private Job', 'Student', 'Other']
};

export default function QuickSearchPopup({ isOpen, onClose }: QuickSearchPopupProps) {
  const navigate = useNavigate();
  const [quickSearch, setQuickSearch] = useState({
    ageRange: [25, 35],
    religion: 'any',
    caste: 'any',
    motherTongue: 'any',
    profession: 'any',
    location: ''
  });

  const handleReset = () => {
    setQuickSearch({
      ageRange: [25, 35],
      religion: 'any',
      caste: 'any',
      motherTongue: 'any',
      profession: 'any',
      location: ''
    });
  };

  const handleSearch = () => {
    // Convert "any" values to empty strings for filtering
    const filtersToPass = {
      ageRange: quickSearch.ageRange,
      religion: quickSearch.religion === 'any' ? '' : quickSearch.religion,
      caste: quickSearch.caste === 'any' ? '' : quickSearch.caste,
      motherTongue: quickSearch.motherTongue === 'any' ? '' : quickSearch.motherTongue,
      profession: quickSearch.profession === 'any' ? '' : quickSearch.profession,
      location: quickSearch.location
    };
    
    // Navigate to partner search page with filters
    navigate('/search', { 
      state: { 
        quickSearchFilters: filtersToPass,
        source: 'dashboard-quick-search'
      }
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl mx-auto border-2 border-rose-200 shadow-2xl">
        <CardHeader className="bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Quick Partner Search</CardTitle>
              <CardDescription className="text-rose-100">
                Find your perfect match with quick filters
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-rose-600"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          {/* Horizontal Filter Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 mb-6">
            {/* Age Range */}
            <div className="lg:col-span-2">
              <Label className="text-sm font-medium mb-2 block">Age Range</Label>
              <div className="space-y-2">
                <Slider
                  value={quickSearch.ageRange}
                  onValueChange={(value) => setQuickSearch({...quickSearch, ageRange: value})}
                  max={60}
                  min={18}
                  step={1}
                  className="mb-2"
                />
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{quickSearch.ageRange[0]} years</span>
                  <span>{quickSearch.ageRange[1]} years</span>
                </div>
              </div>
            </div>

            {/* Religion */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Religion</Label>
              <Select 
                value={quickSearch.religion} 
                onValueChange={(value) => setQuickSearch({...quickSearch, religion: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any religion" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any religion</SelectItem>
                  {searchOptions.religions.map(religion => (
                    <SelectItem key={religion} value={religion.toLowerCase()}>
                      {religion}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Caste */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Caste</Label>
              <Select 
                value={quickSearch.caste} 
                onValueChange={(value) => setQuickSearch({...quickSearch, caste: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any caste" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any caste</SelectItem>
                  {searchOptions.castes.map(caste => (
                    <SelectItem key={caste} value={caste.toLowerCase()}>
                      {caste}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mother Tongue */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Mother Tongue</Label>
              <Select 
                value={quickSearch.motherTongue} 
                onValueChange={(value) => setQuickSearch({...quickSearch, motherTongue: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any language</SelectItem>
                  {searchOptions.motherTongues.map(language => (
                    <SelectItem key={language} value={language.toLowerCase()}>
                      {language}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Profession */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Profession</Label>
              <Select 
                value={quickSearch.profession} 
                onValueChange={(value) => setQuickSearch({...quickSearch, profession: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any profession" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any profession</SelectItem>
                  {searchOptions.professions.map(profession => (
                    <SelectItem key={profession} value={profession.toLowerCase().replace(' ', '-')}>
                      {profession}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Location Input */}
          <div className="mb-6">
            <Label className="text-sm font-medium mb-2 block">Location (Optional)</Label>
            <Input
              placeholder="Enter city or state"
              value={quickSearch.location}
              onChange={(e) => setQuickSearch({...quickSearch, location: e.target.value})}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={handleReset}
              className="bg-transparent border-gray-300"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button
              onClick={handleSearch}
              className="bg-rose-600 hover:bg-rose-700"
            >
              <Search className="h-4 w-4 mr-2" />
              Find Matches
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
